import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations/siteAssets';
import type { ResponsiveContainerProps } from '@wix/thunderbolt-components';
import type { ImageFit, ItemInfoPosition } from '@wix/fast-gallery-library';
import {
  CustomGridScrollDirection,
  buildCustomGridStyles,
  buildSliderStyles,
  buildMasonryStyles,
  buildSlideshowStyles,
  stringToPatternObject,
  buildItemStyles,
  resetContainerCustomStyleSheet,
  resetItemCustomStyleSheet,
} from '@wix/fast-gallery-library';
import _ from 'lodash';
import {
  defaultFastGalleryProps,
  FAST_GALLERY_MAPPERS_SELECTORS,
} from '../constants';
import { convertCustomCssToSelectorToCss } from './FastGallery.galleryUtils';

export type IFastGalleryContainerCarmiProps = {
  containerProps?: ResponsiveContainerProps;
  containerRootClassName?: string;
};

export const props = withCompInfo<any, any, IFastGalleryContainerCarmiProps>()(
  ['compData', 'hasResponsiveLayout'],
  ({ compData, hasResponsiveLayout }, carmiData) => {
    const { items = [], itemClickAction, wixSDKItems } = compData;
    return {
      ...carmiData,
      items,
      wixSDKItems,
      hasResponsiveLayout,
      itemClickAction,
    };
  },
);

export const selectorToCss = withCompInfo<any, any>()(
  ['styleProperties'],
  ({ styleProperties }) => {
    const {
      layout = defaultFastGalleryProps.layout,
      layout_horizontal_gap = defaultFastGalleryProps.layout_horizontal_gap ||
        0,
      layout_horizontal_gap_unit = defaultFastGalleryProps.layout_horizontal_gap_unit as any,
      layout_vertical_gap = defaultFastGalleryProps.layout_vertical_gap || 0,
      layout_vertical_gap_unit = defaultFastGalleryProps.layout_vertical_gap_unit as any,
      item_info_position = defaultFastGalleryProps.item_info_position,
      item_image_fit = defaultFastGalleryProps.item_image_fit,
      enableTitle = defaultFastGalleryProps.enableTitle, // boolean values can be string in live site
      item_elements_alignment = defaultFastGalleryProps.item_elements_alignment,
      customCss = '',
    } = styleProperties;

    const resetContainerStyles =
      resetContainerCustomStyleSheet('.container-reset').cssObject;
    const resetItemStyles = resetItemCustomStyleSheet('.item-reset').cssObject;

    const selectorToCssCustomCss = convertCustomCssToSelectorToCss(customCss);

    let layoutStyles: Record<string, any> = {};

    const itemStyles = buildItemStyles({
      infoPosition: item_info_position as ItemInfoPosition,
      imageFit: item_image_fit as ImageFit,
      enableTitle: enableTitle === true || enableTitle === 'true',
      contentItemsAlignment: item_elements_alignment as any,
    }).cssObject;

    switch (layout) {
      case 'Custom Grid':
        const {
          custom_grid_pattern = '[[1,1,3],[2,1]]',
          custom_grid_opposite_pattern = '300px 400px',
          custom_grid_flip = false,
          custom_grid_mode = CustomGridScrollDirection.vertical,
        } = styleProperties;

        const customGridStyles = buildCustomGridStyles({
          selectors: FAST_GALLERY_MAPPERS_SELECTORS,
          pattern: stringToPatternObject(custom_grid_pattern),
          oppositePattern: custom_grid_opposite_pattern,
          mode: custom_grid_mode as CustomGridScrollDirection,
          // @ts-expect-error
          flip: custom_grid_flip === 'true' || custom_grid_flip === true,
          horizontalGap: +layout_horizontal_gap,
          horizontalGapUnit: layout_horizontal_gap_unit,
          verticalGap: +layout_vertical_gap,
          verticalGapUnit: layout_vertical_gap_unit,
        }).cssObject;

        layoutStyles = customGridStyles;
        break;

      case 'Slider':
        const { slider_item_width = 300 } = styleProperties;
        const sliderStyles = buildSliderStyles({
          selectors: FAST_GALLERY_MAPPERS_SELECTORS,
          horizontalGap: +layout_horizontal_gap,
          horizontalGapUnit: layout_horizontal_gap_unit,
          sliderItemWidth: +slider_item_width,
        }).cssObject;

        layoutStyles = sliderStyles;
        break;

      case 'Masonry':
        const { masonry_density = 10 } = styleProperties;
        const masonryStyles = buildMasonryStyles({
          selectors: FAST_GALLERY_MAPPERS_SELECTORS,
          masonryDensity: +masonry_density,
          horizontalGap: +layout_horizontal_gap,
          horizontalGapUnit: layout_horizontal_gap_unit,
          verticalGap: +layout_vertical_gap,
          verticalGapUnit: layout_vertical_gap_unit,
        }).cssObject;

        layoutStyles = masonryStyles;
        break;

      case 'Slideshow':
        const slideshowStyles = buildSlideshowStyles({
          selectors: FAST_GALLERY_MAPPERS_SELECTORS,
          horizontalGap: +layout_horizontal_gap,
          horizontalGapUnit: layout_horizontal_gap_unit,
        }).cssObject;
        layoutStyles = slideshowStyles;
        break;

      default:
        break;
    }

    const mapperStyles = _.merge(
      {},
      resetContainerStyles,
      resetItemStyles,
      layoutStyles,
      itemStyles,
      selectorToCssCustomCss,
    );

    return mapperStyles;
  },
);

const css = withCompInfo<any, any>()(
  ['styleProperties'],
  ({ styleProperties }) => {
    const { layout = 'Custom Grid' } = styleProperties;
    switch (layout) {
      case 'Custom Grid':
        return {
          overflow: 'unset',
        };
      case 'Slider':
        return {
          overflow: 'scroll',
        };
      case 'Masonry':
        return {
          overflow: 'unset',
        };

      case 'Slideshow':
        return {
          overflow: 'unset',
        };

      default:
        return {
          overflow: 'unset',
        };
    }
  },
);

export default createComponentMapperModel({ props, css, selectorToCss });
